import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Save, Send, Sparkles, Plus, Trash2, ArrowLeft, BarChart3, TestTube2, MailOpen, MousePointerClick, Eye } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import NewsletterPreview from '../components/NewsletterPreview';
import { nlGet, nlCreate, nlUpdate, nlSend, nlAIDraft, nlSubscriberStats } from '../lib/marketingApi';

const inp = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#0035A8] focus:ring-2 focus:ring-[#0035A8]/15';
const lbl = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500';

const EMPTY = { subject: '', preheader: '', category: '', intro: '', sections: [], signoff: '' };

export default function NewsletterEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [cats, setCats] = useState([]);
  const [nl, setNl] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const sent = nl.status === 'sent';
  const catLabel = (slug) => (cats.find((c) => c.slug === slug)?.label || slug);

  useEffect(() => { nlSubscriberStats().then((d) => { setCats(d.categories || []); setNl((n) => (isNew && !n.category ? { ...n, category: (d.categories || [])[0]?.slug || '' } : n)); }).catch(() => {}); }, [isNew]);

  useEffect(() => {
    if (isNew) return;
    nlGet(id).then((d) => setNl(d.newsletter)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k) => (e) => setNl((n) => ({ ...n, [k]: e.target.value }));
  const setSec = (i, k) => (e) => setNl((n) => { const s = [...n.sections]; s[i] = { ...s[i], [k]: e.target.value }; return { ...n, sections: s }; });
  const addSec = () => setNl((n) => ({ ...n, sections: [...n.sections, { heading: '', body: '', source_name: '', source_url: '' }] }));
  const rmSec = (i) => setNl((n) => ({ ...n, sections: n.sections.filter((_, j) => j !== i) }));

  const payload = () => ({ subject: nl.subject, preheader: nl.preheader || '', category: nl.category, intro: nl.intro || '', sections: (nl.sections || []).map((s) => ({ heading: s.heading || '', body: s.body || '', source_name: s.source_name || '', source_url: s.source_url || '' })), signoff: nl.signoff || '' });

  const save = async () => {
    if (!nl.subject?.trim()) { toast.error('Subject is required'); return; }
    setSaving(true);
    try {
      if (isNew) { const d = await nlCreate(payload()); toast.success('Saved'); navigate(`/admin/newsletters/${d.newsletter.id}`); }
      else { const d = await nlUpdate(id, payload()); setNl(d.newsletter); toast.success('Saved'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const aiFill = async () => {
    if (!nl.category) { toast.error('Pick a category first'); return; }
    setDrafting(true);
    try { const d = await nlAIDraft({ category: nl.category }); toast.success('AI draft created'); navigate(`/admin/newsletters/${d.newsletter.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); } finally { setDrafting(false); }
  };

  const doTest = async () => {
    if (!testEmail.includes('@')) { toast.error('Enter a valid test email'); return; }
    if (isNew) { toast.error('Save the newsletter first'); return; }
    setSending(true);
    try { await nlSend(id, { test_email: testEmail }); toast.success(`Test sent to ${testEmail}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); } finally { setSending(false); }
  };

  const doSend = async () => {
    if (isNew) { toast.error('Save first'); return; }
    setConfirmSend(false);
    setSending(true);
    try { const d = await nlSend(id, {}); toast.success(`Sent to ${d.stats?.sent || 0} subscribers${d.companion_blog_id ? '. A round-up blog draft was created in Blogs for your review & publish.' : ''}`); const r = await nlGet(id); setNl(r.newsletter); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };

  if (loading) return (<div className="min-h-screen bg-slate-50"><AdminHeader active="content" /><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);

  const st = nl.stats || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="newsletter-editor">
      <AdminHeader active="content" actions={(
        <div className="flex items-center gap-2">
          {canEdit && !sent && (
            <button onClick={aiFill} disabled={drafting} data-testid="nl-editor-ai-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#0035A8] px-3 text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8]/5 disabled:opacity-60">{drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Regenerate</button>
          )}
          {canEdit && !sent && (
            <button onClick={save} disabled={saving} data-testid="nl-editor-save-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
          )}
        </div>
      )} />

      {confirmSend && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="nl-send-confirm-modal" onClick={() => setConfirmSend(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><Send className="h-5 w-5" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Send this newsletter?</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  Once you send, “{nl.subject}” will be emailed to all subscribers of <b>{catLabel(nl.category)}</b> and a round-up blog draft will be created for review. This cannot be undone. Are you sure you want to go ahead?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmSend(false)} data-testid="nl-send-confirm-cancel" className="inline-flex h-10 items-center rounded-md border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={doSend} disabled={sending} data-testid="nl-send-confirm-yes" className="inline-flex h-10 items-center gap-1.5 rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Yes, send now</button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-[1500px] px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/newsletters')} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Newsletters</button>

        {!isNew && (
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500" data-testid="nl-audit">
            <span>Created by <b className="text-slate-700">{nl.created_by || '—'}</b>{nl.created_at ? ` · ${new Date(nl.created_at).toLocaleString()}` : ''}</span>
            {nl.sent_at ? <span>Sent by <b className="text-slate-700">{nl.sent_by || '—'}</b> · {new Date(nl.sent_at).toLocaleString()}</span> : <span className="text-slate-400">Not sent yet</span>}
          </div>
        )}

        {sent && (
          <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="nl-sent-stats">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><BarChart3 className="h-4 w-4" /> Sent {nl.sent_at ? new Date(nl.sent_at).toLocaleString() : ''}</div>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5">
              {[['Recipients', st.recipients], ['Sent', st.sent], ['Opened', st.opened], ['Clicked', st.clicked], ['Bounced', st.bounced]].map(([l, v]) => (
                <div key={l} className="rounded-md bg-white p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{l}</div>
                  <div className="mt-0.5 text-lg font-bold text-slate-800">{(v ?? 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-emerald-700">Opens are counted via a tracking pixel; clicks via link redirects. Delivery/bounce/complaint totals populate once SES event notifications (SNS) are enabled.</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* ---- Left: form ---- */}
          <div className="space-y-4 lg:col-span-7">
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className={lbl}>Subject line</label>
                  <input className={inp} value={nl.subject} onChange={set('subject')} disabled={sent} data-testid="nl-subject" placeholder="Weekly market update…" />
                </div>
                <div>
                  <label className={lbl}>Category</label>
                  <select className={inp} value={nl.category} onChange={set('category')} disabled={sent || !isNew} data-testid="nl-category">
                    {cats.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Preheader (inbox preview)</label>
                <input className={inp} value={nl.preheader || ''} onChange={set('preheader')} disabled={sent} data-testid="nl-preheader" />
              </div>
              <div>
                <label className={lbl}>Intro</label>
                <textarea rows={3} className={inp} value={nl.intro || ''} onChange={set('intro')} disabled={sent} data-testid="nl-intro" />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className={lbl}>Sections ({(nl.sections || []).length})</label>
                  {!sent && <button onClick={addSec} data-testid="nl-add-section" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0035A8] hover:underline"><Plus className="h-3.5 w-3.5" /> Add section</button>}
                </div>
                <div className="space-y-3">
                  {(nl.sections || []).map((s, i) => (
                    <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`nl-section-${i}`}>
                      <div className="flex items-start gap-2">
                        <input className={`${inp} font-semibold`} value={s.heading || ''} onChange={setSec(i, 'heading')} disabled={sent} placeholder="Section heading" />
                        {!sent && <button onClick={() => rmSec(i)} className="mt-1 rounded p-1.5 text-slate-400 hover:bg-white hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                      <textarea rows={3} className={`${inp} mt-2`} value={s.body || ''} onChange={setSec(i, 'body')} disabled={sent} placeholder="Summary + why it matters" />
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <input className={inp} value={s.source_name || ''} onChange={setSec(i, 'source_name')} disabled={sent} placeholder="Source name" />
                        <input className={inp} value={s.source_url || ''} onChange={setSec(i, 'source_url')} disabled={sent} placeholder="https://source-url" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className={lbl}>Sign-off</label>
                <input className={inp} value={nl.signoff || ''} onChange={set('signoff')} disabled={sent} data-testid="nl-signoff" />
              </div>
            </div>

            {!sent && canEdit && (
              <div className="flex items-center justify-end rounded-lg border border-slate-200 bg-white p-5">
                <button onClick={() => { if (isNew) { toast.error('Save first'); return; } setConfirmSend(true); }} disabled={sending} data-testid="nl-send-all-btn" className="inline-flex h-[38px] items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Approve & send to subscribers</button>
              </div>
            )}
          </div>

          {/* ---- Right: live email preview ---- */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <Eye className="h-3.5 w-3.5" /> Live email preview
              </div>
              {canEdit && (
                <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="nl-test-panel">
                  <label className={lbl}>Send a test email</label>
                  <div className="flex gap-2">
                    <input className={inp} type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="nl-test-email" placeholder="name@company.com" />
                    <button onClick={doTest} disabled={sending} data-testid="nl-test-send-btn" className="inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-md border border-[#0035A8] px-3.5 text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8]/5 disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />} Send test</button>
                  </div>
                  <p className="mt-1.5 text-xs text-slate-400">Delivers this exact email to the address above (save the draft first).</p>
                </div>
              )}
              <NewsletterPreview nl={nl} categoryLabel={catLabel(nl.category)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
